import { Alert, Autocomplete, TextField } from '@mui/material';
import React from 'react';
import { BillingPolicyAnswerField, BillingPolicyAnswers, BillingPolicyPublicDescriptor } from 'services/billingPolicy/contracts/BillingPolicy';
import { preselectRomanianAddress } from '../romanianLocalities';
import { NamedAddressSuggestion, searchPhotonAddresses } from '../PhotonAddressSearch';

export const PolicyAddressSearch = ({ document, answers = {}, disabled, locale, label, failureLabel, incompatibleLabel, onSelect }: {
    document: BillingPolicyPublicDescriptor['documentCases'][number]; disabled: boolean; locale: string;
    answers?: BillingPolicyAnswers;
    label: string; failureLabel: string; incompatibleLabel: string; onSelect: (values: BillingPolicyAnswers) => void;
}) => {
    const [query, setQuery] = React.useState('');
    const [inputValue, setInputValue] = React.useState('');
    const [selection, setSelection] = React.useState<NamedAddressSuggestion | null>(null);
    const [options, setOptions] = React.useState<NamedAddressSuggestion[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const [incompatible, setIncompatible] = React.useState(false);
    const countrySchema = document.inputSchema.properties.addressCountryCode;
    const country = countrySchema?.type === 'string' ? countrySchema.const
        || (typeof answers.addressCountryCode === 'string' ? answers.addressCountryCode : undefined)
        || (countrySchema.enum?.length === 1 ? countrySchema.enum[0] : undefined) : undefined;
    const requireStreet = document.ui.find(control => control.control === 'address-search')?.addressSearch?.requireStreet ?? true;
    const previousCountry = React.useRef(country);
    React.useEffect(() => {
        if (previousCountry.current === country) return;
        previousCountry.current = country;
        // Selecting a suggestion may itself fill the country. Retain that matching selection.
        if (selection && selection.countryCode === country) return;
        setSelection(null); setInputValue(''); setQuery('');
    }, [country, selection]);
    React.useEffect(() => {
        const controller = new AbortController();
        setOptions([]); setFailed(false); setIncompatible(false); setLoading(false);
        if (disabled || query.trim().length < 3) return () => controller.abort();
        const timer = setTimeout(() => {
            setLoading(true);
            searchPhotonAddresses(query.trim(), controller.signal, country, locale, { requireStreet }).then(results => {
                if (!controller.signal.aborted) { setOptions(results); setFailed(false); }
            }).catch(() => { if (!controller.signal.aborted) setFailed(true); })
                .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        }, 350);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [query, country, locale, disabled, requireStreet]);
    return <>
        <Autocomplete options={options} value={selection} inputValue={inputValue} disabled={disabled} loading={loading} filterOptions={items => items}
            getOptionLabel={item => item.label} isOptionEqualToValue={(a, b) => a.label === b.label}
            onInputChange={(_, value, reason) => {
                setInputValue(value);
                if (reason === 'input' || reason === 'clear') setQuery(value);
                if (!disabled && (reason === 'clear' || (reason === 'input' && value === '' && inputValue !== ''))) {
                    setSelection(null); setOptions([]); setFailed(false); setIncompatible(false);
                    const cleared: BillingPolicyAnswers = {};
                    for (const [field, schema] of Object.entries(document.inputSchema.properties)) {
                        if (!field.startsWith('address') || schema.const !== undefined) continue;
                        // A policy with only one country must retain that country.
                        if (field === 'addressCountryCode' && schema.type === 'string' && schema.enum?.length === 1) continue;
                        cleared[field as BillingPolicyAnswerField] = '';
                    }
                    onSelect(cleared);
                }
            }}
            onChange={(_, value) => {
                setSelection(value);
                if (!value) return;
                const place = document.ui.some(control => control.control === 'ro-city') ? preselectRomanianAddress(value) : value;
                const values: BillingPolicyAnswers = { addressLine1: value.line1, addressCountryCode: place.countryCode,
                    addressRegion: place.region, addressCity: place.city, addressPostalCode: value.postalCode || '',
                    addressLine2: '' };
                // Check the whole suggestion before editAddress preserves any fixed fields.
                const conflicts = Object.entries(values).some(([field, proposed]) => {
                    const schema = document.inputSchema.properties[field as BillingPolicyAnswerField];
                    if (schema?.type !== 'string') return false;
                    const fixed = schema.const ?? (schema.enum?.length === 1 ? schema.enum[0] : undefined);
                    return fixed !== undefined && proposed !== fixed;
                });
                setIncompatible(conflicts);
                setFailed(false);
                if (!conflicts) onSelect(values);
            }} renderInput={params => <TextField {...params} label={label} />} />
        {failed || incompatible ? <Alert severity="warning">{incompatible ? incompatibleLabel : failureLabel}</Alert> : null}
    </>;
};
